import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, map, of, tap } from 'rxjs';
import { TenantCapability, TenantCapabilitiesResponse } from '../models/api.models';
import { environment } from '../../../environments/environment';

const ADMIN_BASE = `${environment.apiBaseUrl}/v1/admin`;

@Injectable({ providedIn: 'root' })
export class CapabilitiesService {
  private readonly http = inject(HttpClient);

  private readonly _capabilities = signal<TenantCapability[]>([]);
  readonly capabilities = this._capabilities.asReadonly();

  /** Returns true if the caller holds the given capability for the active tenant. */
  has(cap: TenantCapability): boolean {
    return this._capabilities().includes(cap);
  }

  /** Load the caller's own capabilities for a tenant. Call after tenant switch or login. */
  loadForCaller(tenantId: string, userId: string): void {
    this.http
      .get<TenantCapabilitiesResponse>(
        `${ADMIN_BASE}/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}/capabilities`
      )
      .subscribe({
        next: (res) => this._capabilities.set(res.capabilities.map((c) => c.capability)),
        error: () => this._capabilities.set([]),
      });
  }

  /** Awaitable capability fetch (unlike `loadForCaller`) so route guards can resolve before deciding access. */
  fetchForCaller(tenantId: string, userId: string): Observable<TenantCapability[]> {
    return this.loadForMember(tenantId, userId).pipe(
      map((res) => res.capabilities.map((c) => c.capability)),
      tap((caps) => this._capabilities.set(caps)),
      catchError(() => {
        this._capabilities.set([]);
        return of<TenantCapability[]>([]);
      }),
    );
  }

  /** Clear cached capabilities (e.g. on logout or tenant switch before reload). */
  clear(): void {
    this._capabilities.set([]);
  }

  /** Grant a capability to a member (owner only). Returns an Observable - caller must subscribe. */
  grant(tenantId: string, userId: string, cap: TenantCapability) {
    return this.http.post<{ status: string }>(
      `${ADMIN_BASE}/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}/capabilities`,
      { capability: cap }
    );
  }

  /** Revoke a capability from a member (owner only). Returns an Observable - caller must subscribe. */
  revoke(tenantId: string, userId: string, cap: TenantCapability) {
    return this.http.delete<{ status: string }>(
      `${ADMIN_BASE}/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}/capabilities/${encodeURIComponent(cap)}`
    );
  }

  /** Load capabilities for a specific member (used by owner-facing management UI). */
  loadForMember(tenantId: string, userId: string) {
    return this.http.get<TenantCapabilitiesResponse>(
      `${ADMIN_BASE}/tenants/${encodeURIComponent(tenantId)}/members/${encodeURIComponent(userId)}/capabilities`
    );
  }
}
